/**
 * Report Service
 * Handles all report generation and management API operations
 * 
 * @module services/reportService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Report Service Class
 * Provides methods for report generation, scheduling, export, and template management
 * 
 * @class ReportService
 * @extends BaseService
 * 
 * @example
 * import reportService from './services/reportService';
 * 
 * // Generate report
 * const report = await reportService.generate({
 *   type: 'financial',
 *   period: 'month',
 *   format: 'pdf'
 * });
 * 
 * // Schedule report
 * await reportService.schedule({
 *   templateId: 'template-123',
 *   frequency: 'weekly',
 *   recipients: ['admin@ecomotech.com']
 * });
 */
class ReportService extends BaseService {
  constructor() {
    super('/reports');
  }

  /**
   * Get all reports with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.type] - Filter by type (financial, inventory, user, department, task, budget)
   * @param {string} [params.status] - Filter by status (generating, completed, failed, scheduled)
   * @param {string} [params.createdBy] - Filter by creator
   * @param {string} [params.startDate] - Filter by creation start date
   * @param {string} [params.endDate] - Filter by creation end date
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get report by ID
   * 
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} Report details
   */
  async getById(reportId) {
    return this.get(`/${reportId}`);
  }

  /**
   * Generate report
   * 
   * @param {Object} reportConfig - Report configuration
   * @param {string} reportConfig.type - Report type
   * @param {string} reportConfig.name - Report name
   * @param {string} [reportConfig.format='pdf'] - Output format (pdf, xlsx, csv, html)
   * @param {Object} reportConfig.parameters - Report parameters (dates, filters, etc.)
   * @param {string} [reportConfig.templateId] - Template ID to use
   * @param {boolean} [reportConfig.async=false] - Generate asynchronously
   * @returns {Promise<Object>} Generated report or generation job
   */
  async generate(reportConfig) {
    return this.postWithCancel('', 'report-generation', reportConfig);
  }

  /**
   * Download report file
   * 
   * @param {string} reportId - Report ID
   * @returns {Promise<Blob>} Report file
   */
  async download(reportId) {
    return this.get(`/${reportId}/download`, {
      responseType: 'blob'
    });
  }

  /**
   * Delete report
   * 
   * @param {string} reportId - Report ID
   * @returns {Promise<void>}
   */
  async delete(reportId) {
    return this.delete(`/${reportId}`);
  }

  /**
   * Get report templates
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.type] - Filter by report type
   * @param {string} [params.category] - Filter by category
   * @returns {Promise<Array>} Report templates
   */
  async getTemplates(params = {}) {
    return this.get('/templates', { params });
  }

  /**
   * Get template by ID
   * 
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Template details
   */
  async getTemplateById(templateId) {
    return this.get(`/templates/${templateId}`);
  }

  /**
   * Create report template
   * 
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name
   * @param {string} templateData.type - Report type
   * @param {string} [templateData.description] - Description
   * @param {Object} templateData.config - Template configuration
   * @param {Array<string>} [templateData.tags] - Tags
   * @returns {Promise<Object>} Created template
   */
  async createTemplate(templateData) {
    return this.post('/templates', templateData);
  }

  /**
   * Update report template
   * 
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template
   */
  async updateTemplate(templateId, templateData) {
    return this.put(`/templates/${templateId}`, templateData);
  }

  /**
   * Delete report template
   * 
   * @param {string} templateId - Template ID
   * @returns {Promise<void>}
   */
  async deleteTemplate(templateId) {
    return this.delete(`/templates/${templateId}`);
  }

  /**
   * Get scheduled reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {boolean} [params.active] - Filter by active status
   * @returns {Promise<Array>} Scheduled reports
   */
  async getScheduled(params = {}) {
    return this.get('/scheduled', { params });
  }

  /**
   * Schedule report generation
   * 
   * @param {Object} scheduleData - Schedule data
   * @param {string} scheduleData.name - Schedule name
   * @param {string} scheduleData.templateId - Template ID
   * @param {string} scheduleData.frequency - Frequency (daily, weekly, monthly, quarterly, yearly)
   * @param {string} [scheduleData.time] - Time to generate (HH:mm)
   * @param {Array<string>} scheduleData.recipients - Email recipients
   * @param {Object} [scheduleData.parameters] - Default parameters
   * @param {boolean} [scheduleData.active=true] - Active status
   * @returns {Promise<Object>} Created schedule
   */
  async schedule(scheduleData) {
    return this.post('/scheduled', scheduleData);
  }

  /**
   * Update scheduled report
   * 
   * @param {string} scheduleId - Schedule ID
   * @param {Object} scheduleData - Updated schedule data
   * @returns {Promise<Object>} Updated schedule
   */
  async updateSchedule(scheduleId, scheduleData) {
    return this.put(`/scheduled/${scheduleId}`, scheduleData);
  }

  /**
   * Delete scheduled report
   * 
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<void>}
   */
  async deleteSchedule(scheduleId) {
    return this.delete(`/scheduled/${scheduleId}`);
  }

  /**
   * Pause scheduled report
   * 
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Updated schedule
   */
  async pauseSchedule(scheduleId) {
    return this.patch(`/scheduled/${scheduleId}/pause`);
  }

  /**
   * Resume scheduled report
   * 
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Updated schedule
   */
  async resumeSchedule(scheduleId) {
    return this.patch(`/scheduled/${scheduleId}/resume`);
  }

  /**
   * Get financial reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} params.startDate - Start date
   * @param {string} params.endDate - End date
   * @param {string} [params.reportType] - Specific financial report type
   * @returns {Promise<Array>} Financial reports
   */
  async getFinancialReports(params) {
    return this.get('/financial', { params });
  }

  /**
   * Generate profit & loss report
   * 
   * @param {Object} params - Report parameters
   * @param {string} params.startDate - Start date
   * @param {string} params.endDate - End date
   * @param {string} [params.format='pdf'] - Output format
   * @returns {Promise<Object>} P&L report
   */
  async generateProfitLoss(params) {
    return this.post('/financial/profit-loss', params);
  }

  /**
   * Generate balance sheet report
   * 
   * @param {Object} params - Report parameters
   * @param {string} [params.asOfDate] - Date for balance sheet
   * @param {string} [params.format='pdf'] - Output format
   * @returns {Promise<Object>} Balance sheet report
   */
  async generateBalanceSheet(params) {
    return this.post('/financial/balance-sheet', params);
  }

  /**
   * Generate cash flow report
   * 
   * @param {Object} params - Report parameters
   * @param {string} params.startDate - Start date
   * @param {string} params.endDate - End date
   * @param {string} [params.format='pdf'] - Output format
   * @returns {Promise<Object>} Cash flow report
   */
  async generateCashFlow(params) {
    return this.post('/financial/cash-flow', params);
  }

  /**
   * Get inventory reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Inventory reports
   */
  async getInventoryReports(params = {}) {
    return this.get('/inventory', { params });
  }

  /**
   * Generate inventory valuation report
   * 
   * @param {Object} [params={}] - Report parameters
   * @param {string} [params.asOfDate] - Date for valuation
   * @param {string} [params.format='xlsx'] - Output format
   * @returns {Promise<Object>} Inventory valuation report
   */
  async generateInventoryValuation(params = {}) {
    return this.post('/inventory/valuation', params);
  }

  /**
   * Generate stock movement report
   * 
   * @param {Object} params - Report parameters
   * @param {string} params.startDate - Start date
   * @param {string} params.endDate - End date
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.format='xlsx'] - Output format
   * @returns {Promise<Object>} Stock movement report
   */
  async generateStockMovement(params) {
    return this.post('/inventory/stock-movement', params);
  }

  /**
   * Get department reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.departmentId] - Filter by department
   * @returns {Promise<Array>} Department reports
   */
  async getDepartmentReports(params = {}) {
    return this.get('/department', { params });
  }

  /**
   * Generate department performance report
   * 
   * @param {Object} params - Report parameters
   * @param {string} params.departmentId - Department ID
   * @param {string} params.startDate - Start date
   * @param {string} params.endDate - End date
   * @param {string} [params.format='pdf'] - Output format
   * @returns {Promise<Object>} Performance report
   */
  async generateDepartmentPerformance(params) {
    return this.post('/department/performance', params);
  }

  /**
   * Get budget reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.fiscalYear] - Filter by fiscal year
   * @returns {Promise<Array>} Budget reports
   */
  async getBudgetReports(params = {}) {
    return this.get('/budget', { params });
  }

  /**
   * Generate budget variance report
   * 
   * @param {Object} params - Report parameters
   * @param {number} params.fiscalYear - Fiscal year
   * @param {string} [params.department] - Filter by department
   * @param {string} [params.format='pdf'] - Output format
   * @returns {Promise<Object>} Variance report
   */
  async generateBudgetVariance(params) {
    return this.post('/budget/variance', params);
  }

  /**
   * Share report with users
   * 
   * @param {string} reportId - Report ID
   * @param {Object} shareData - Share data
   * @param {Array<string>} shareData.userIds - User IDs to share with
   * @param {Array<string>} [shareData.emails] - Email addresses
   * @param {string} [shareData.message] - Share message
   * @returns {Promise<Object>} Share result
   */
  async share(reportId, shareData) {
    return this.post(`/${reportId}/share`, shareData);
  }

  /**
   * Get report statistics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Report statistics
   */
  async getStatistics(params = {}) {
    return this.get('/statistics', { params });
  }

  /**
   * Get report generation status
   * 
   * @param {string} jobId - Generation job ID
   * @returns {Promise<Object>} Job status
   */
  async getGenerationStatus(jobId) {
    return this.get(`/jobs/${jobId}/status`);
  }

  /**
   * Cancel report generation
   * 
   * @param {string} jobId - Generation job ID
   * @returns {Promise<void>}
   */
  async cancelGeneration(jobId) {
    return this.delete(`/jobs/${jobId}`);
  }

  /**
   * Search reports with cancellation
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {string} [options.type] - Filter by type
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching reports
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'report-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Export multiple reports as archive
   * 
   * @param {Object} exportData - Export data
   * @param {Array<string>} exportData.reportIds - Report IDs to export
   * @param {string} [exportData.format='zip'] - Archive format
   * @returns {Promise<Blob>} Archive file
   */
  async exportMultiple(exportData) {
    return this.post('/export-multiple', exportData, {
      responseType: 'blob'
    });
  }

  /**
   * Get recent reports
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.limit=10] - Max results
   * @returns {Promise<Array>} Recent reports
   */
  async getRecent(params = {}) {
    return this.get('/recent', { params });
  }

  /**
   * Get favorite reports
   * 
   * @returns {Promise<Array>} Favorite reports
   */
  async getFavorites() {
    return this.get('/favorites');
  }

  /**
   * Add report to favorites
   * 
   * @param {string} reportId - Report ID
   * @returns {Promise<void>}
   */
  async addToFavorites(reportId) {
    return this.post(`/${reportId}/favorite`);
  }

  /**
   * Remove report from favorites
   * 
   * @param {string} reportId - Report ID
   * @returns {Promise<void>}
   */
  async removeFromFavorites(reportId) {
    return this.delete(`/${reportId}/favorite`);
  }
}

// Export singleton instance
const reportService = new ReportService();
export default reportService;
